import { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './CalendarPopover.css';

const VIEWPORT_MARGIN = 8;
const ANCHOR_GAP = 4;

function CalendarPopover({ anchorRef, onClose, width, initialFocus, className, children, ...attributes }) {
    const popoverRef = useRef(null);

    useLayoutEffect(() => {
        const popover = popoverRef.current;
        const anchor = anchorRef.current;
        if (!popover || !anchor) return;

        const positionPopover = () => {
            const anchorRect = anchor.getBoundingClientRect();
            const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
            const viewportHeight = window.innerHeight;

            // Measure at the available width before deciding which side has room.
            popover.style.width = 'max-content';
            popover.style.maxHeight = 'none';
            const preferredWidth = width ?? Math.max(anchorRect.width, popover.getBoundingClientRect().width);
            const popoverWidth = Math.min(preferredWidth, viewportWidth - VIEWPORT_MARGIN * 2);
            popover.style.width = `${popoverWidth}px`;

            const naturalHeight = popover.getBoundingClientRect().height;
            const spaceBelow = Math.max(0, viewportHeight - anchorRect.bottom - ANCHOR_GAP - VIEWPORT_MARGIN);
            const spaceAbove = Math.max(0, anchorRect.top - ANCHOR_GAP - VIEWPORT_MARGIN);
            const opensAbove = naturalHeight > spaceBelow && spaceAbove > spaceBelow;
            const maxHeight = opensAbove ? spaceAbove : spaceBelow;
            const height = Math.min(naturalHeight, maxHeight);
            const top = opensAbove ? anchorRect.top - ANCHOR_GAP - height : anchorRect.bottom + ANCHOR_GAP;
            const left = Math.max(VIEWPORT_MARGIN, Math.min(anchorRect.left, viewportWidth - popoverWidth - VIEWPORT_MARGIN));

            Object.assign(popover.style, {
                top: `${Math.max(VIEWPORT_MARGIN, top)}px`,
                left: `${left}px`,
                maxHeight: `${maxHeight}px`,
                visibility: 'visible',
            });
        };

        positionPopover();
        if (initialFocus) {
            popover.querySelector(initialFocus)?.focus({ preventScroll: true });
        }
        window.addEventListener('resize', positionPopover);
        return () => {
            window.removeEventListener('resize', positionPopover);
            if (popover.contains(document.activeElement)) {
                anchor.querySelector('[role="combobox"]')?.focus({ preventScroll: true });
            }
        };
    }, [anchorRef, width, initialFocus]);

    useEffect(() => {
        const scrollPositions = new Map();
        for (let ancestor = anchorRef.current; ancestor; ancestor = ancestor.parentElement) {
            scrollPositions.set(ancestor, [ancestor.scrollLeft, ancestor.scrollTop]);
        }
        scrollPositions.set(document, [window.scrollX, window.scrollY]);

        const closeOutside = event => {
            if (!anchorRef.current?.contains(event.target) && !popoverRef.current?.contains(event.target)) {
                onClose();
            }
        };
        const closeOnScroll = event => {
            const previous = scrollPositions.get(event.target);
            if (!previous) return;

            const current = event.target === document
                ? [window.scrollX, window.scrollY]
                : [event.target.scrollLeft, event.target.scrollTop];
            // Ignore queued scroll events from bringing the trigger into view.
            // Scrolls inside the portaled menu are not in this ancestor map.
            if (current[0] !== previous[0] || current[1] !== previous[1]) onClose();
        };
        const closeOnEscape = event => {
            if (event.key === 'Escape') {
                event.preventDefault();
                onClose();
            }
        };

        document.addEventListener('mousedown', closeOutside);
        document.addEventListener('focusin', closeOutside);
        document.addEventListener('keydown', closeOnEscape);
        window.addEventListener('scroll', closeOnScroll, true);
        return () => {
            document.removeEventListener('mousedown', closeOutside);
            document.removeEventListener('focusin', closeOutside);
            document.removeEventListener('keydown', closeOnEscape);
            window.removeEventListener('scroll', closeOnScroll, true);
        };
    }, [anchorRef, onClose]);

    return createPortal(
        <div
            {...attributes}
            ref={popoverRef}
            className={`calendar-popover ${className}`}
            style={{ position: 'fixed', visibility: 'hidden' }}
        >
            {children}
        </div>,
        document.body
    );
}

export default CalendarPopover;
